import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, useIsHost } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';
import { GENERATING_MESSAGES } from '@youraiquiz/shared';

const GeneratingView = () => {
  const { gameState } = useGameStore();
  const isHost = useIsHost();
  const { startQuiz } = useGameActions();
  const { leaveRoom } = useSocket();

  const [msgIdx, setMsgIdx] = useState(0);
  const isReady = gameState.status === 'QUIZ_READY';

  useEffect(() => {
    if (!isReady) {
      const interval = setInterval(() => {
        setMsgIdx(i => (i + 1) % GENERATING_MESSAGES.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isReady]);

  const sortedByDownvotes = [...gameState.categories].sort((a, b) => b.downvotes - a.downvotes);
  const removalLimit = gameState.config.vetoRemovalCount;
  const deadCategories =
    sortedByDownvotes.length > removalLimit ? sortedByDownvotes.slice(0, removalLimit) : [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="text-center space-y-12 w-full max-w-2xl px-4 flex flex-col items-center"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="self-start text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
      >
        LEAVE ROOM
      </button>

      {/* Spinner/Checkmark */}
      <div className="relative inline-block">
        <motion.div
          animate={isReady ? { scale: [1, 1.1, 1] } : { rotate: 360 }}
          transition={
            isReady
              ? { duration: 2, repeat: Infinity }
              : { duration: 4, repeat: Infinity, ease: 'linear' }
          }
          className={`w-48 h-48 border-8 rounded-full ${
            isReady
              ? 'border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.3)]'
              : 'border-t-neon-purple border-r-transparent border-b-neon-blue border-l-transparent'
          }`}
        />
        <div className="absolute inset-0 flex items-center justify-center text-6xl">
          {isReady ? '✅' : '🍹'}
        </div>
      </div>

      {/* Voted Out Categories */}
      {deadCategories.length > 0 && !isReady && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6 glass p-8 rounded-3xl border-2 border-red-500/20 w-full"
        >
          <div className="space-y-1">
            <h3 className="text-red-500 font-black uppercase text-xs tracking-[0.4em]">
              Voted Out By Players
            </h3>
            <div className="flex flex-wrap justify-center gap-2">
              {deadCategories.map(c => (
                <span
                  key={c.id}
                  className="text-white/40 line-through font-black text-xl uppercase italic"
                >
                  {c.text}
                </span>
              ))}
            </div>
          </div>

          <AnimatePresence>
            {gameState.vetoCommentary && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
                <p className="text-2xl font-black italic text-white leading-tight">
                  "{gameState.vetoCommentary}"
                </p>
                <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest">
                  — The AI Bartender
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Status / Start Button */}
      <div className="space-y-4 w-full">
        {isReady ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="space-y-2">
              <h2 className="text-5xl font-black italic tracking-tighter uppercase text-green-400">
                Quiz Distilled!
              </h2>
              <p className="text-gray-400 font-bold uppercase tracking-[0.3em] text-sm">
                Everything is ready for the first round
              </p>
            </div>

            <div className="glass p-6 rounded-3xl border border-white/10 space-y-4">
              <div className="flex justify-around items-center">
                <div className="text-center">
                  <div className="text-3xl font-black text-neon-blue">{gameState.questions.length}</div>
                  <div className="text-[10px] font-black uppercase text-white/40">Questions</div>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="text-center">
                  <div className="text-3xl font-black text-neon-purple">{gameState.players.length}</div>
                  <div className="text-[10px] font-black uppercase text-white/40">Victims</div>
                </div>
              </div>
            </div>

            {isHost ? (
              <button
                onClick={startQuiz}
                className="w-full max-w-sm bg-neon-purple py-6 rounded-3xl font-black text-3xl hover:scale-105 active:scale-95 transition-all shadow-[0_0_50px_rgba(217,70,239,0.4)] border-t-4 border-purple-400 uppercase italic tracking-tighter"
              >
                START THE CHAOS
              </button>
            ) : (
              <div className="text-neon-purple font-black text-xl animate-pulse uppercase italic">
                Waiting for host to pull the trigger...
              </div>
            )}
          </motion.div>
        ) : (
          <>
            <h2 className="text-5xl font-black italic tracking-tighter animate-pulse">
              {GENERATING_MESSAGES[msgIdx]}
            </h2>
            <p className="text-gray-500 font-bold uppercase tracking-[0.3em] text-sm">
              Gemini AI is cooking your quiz
            </p>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default GeneratingView;
